import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { format, addDays, subDays } from "date-fns";

// Helper function to adjust psalm numbers between different Bible traditions
const adjustPsalmForLanguage = (psalmNum: number, targetLang: string): number => {
  // Base numbering is English/Hebrew (KJV)
  // Russian uses Septuagint numbering which is different
  
  if (targetLang === 'ru') {
    // Convert from English (Hebrew) to Russian (Septuagint)
    if (psalmNum >= 11 && psalmNum <= 113) return psalmNum - 1;
    if (psalmNum === 114 || psalmNum === 115) return 113;
    if (psalmNum === 116) return 114;
    if (psalmNum >= 117 && psalmNum <= 146) return psalmNum - 1;
    if (psalmNum === 147) return 146;
  }
  
  // English and Spanish use the same numbering (Hebrew)
  return psalmNum;
};

interface DailyReading {
  date: Date;
  oldTestament: string;
  newTestament: string;
  psalm: string;
  proverb: string;
}

const DailyBibleReading = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Get day of year for cycling through readings
  const getDayOfYear = (date: Date) => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  // Generate reading plan based on date
  const generateDailyReading = (date: Date): DailyReading => {
    const dayOfYear = getDayOfYear(date);
    
    // Book definitions with chapter counts
    const otBooks = [
      { key: "genesis", chapters: 50 },
      { key: "exodus", chapters: 40 },
      { key: "leviticus", chapters: 27 },
      { key: "numbers", chapters: 36 },
      { key: "deuteronomy", chapters: 34 },
      { key: "joshua", chapters: 24 },
      { key: "judges", chapters: 21 },
      { key: "ruth", chapters: 4 },
      { key: "1samuel", chapters: 31 },
      { key: "2samuel", chapters: 24 }
    ];
    
    const ntBooks = [
      { key: "matthew", chapters: 28 },
      { key: "mark", chapters: 16 },
      { key: "luke", chapters: 24 },
      { key: "john", chapters: 21 },
      { key: "acts", chapters: 28 },
      { key: "romans", chapters: 16 },
      { key: "1corinthians", chapters: 16 },
      { key: "2corinthians", chapters: 13 },
      { key: "galatians", chapters: 6 },
      { key: "ephesians", chapters: 6 }
    ];
    
    // Get book names from Bible.tsx BOOK_NAMES mapping
    const BOOK_NAMES: Record<string, Record<string, string>> = {
      en: {
        genesis: "Genesis", exodus: "Exodus", leviticus: "Leviticus", numbers: "Numbers",
        deuteronomy: "Deuteronomy", joshua: "Joshua", judges: "Judges", ruth: "Ruth",
        "1samuel": "1 Samuel", "2samuel": "2 Samuel", psalms: "Psalms", proverbs: "Proverbs",
        matthew: "Matthew", mark: "Mark", luke: "Luke", john: "John", acts: "Acts",
        romans: "Romans", "1corinthians": "1 Corinthians", "2corinthians": "2 Corinthians",
        galatians: "Galatians", ephesians: "Ephesians"
      },
      ru: {
        genesis: "Бытие", exodus: "Исход", leviticus: "Левит", numbers: "Числа",
        deuteronomy: "Второзаконие", joshua: "Иисус Навин", judges: "Судьи", ruth: "Руфь",
        "1samuel": "1 Царств", "2samuel": "2 Царств", psalms: "Псалтирь", proverbs: "Притчи",
        matthew: "Матфея", mark: "Марка", luke: "Луки", john: "Иоанна", acts: "Деяния",
        romans: "Римлянам", "1corinthians": "1 Коринфянам", "2corinthians": "2 Коринфянам",
        galatians: "Галатам", ephesians: "Ефесянам"
      },
      es: {
        genesis: "Génesis", exodus: "Éxodo", leviticus: "Levítico", numbers: "Números",
        deuteronomy: "Deuteronomio", joshua: "Josué", judges: "Jueces", ruth: "Rut",
        "1samuel": "1 Samuel", "2samuel": "2 Samuel", psalms: "Salmos", proverbs: "Proverbios",
        matthew: "Mateo", mark: "Marcos", luke: "Lucas", john: "Juan", acts: "Hechos",
        romans: "Romanos", "1corinthians": "1 Corintios", "2corinthians": "2 Corintios",
        galatians: "Gálatas", ephesians: "Efesios"
      }
    };
    
    const bookNames = BOOK_NAMES[language] || BOOK_NAMES.en;
    
    // Calculate which book and chapter based on cycling through all chapters
    const getBookAndChapter = (books: typeof otBooks, offset: number) => {
      let totalChapters = 0;
      const bookIndex = offset % books.length;
      const book = books[bookIndex];
      
      // Calculate which chapter within the book (cycling through available chapters)
      const chapterIndex = Math.floor(offset / books.length) % book.chapters;
      const chapter = chapterIndex + 1;
      
      return { book, chapter };
    };
    
    const otReading = getBookAndChapter(otBooks, dayOfYear);
    const ntReading = getBookAndChapter(ntBooks, dayOfYear);
    const basePsalmNum = (dayOfYear % 150) + 1; // Base is English/Hebrew numbering
    const adjustedPsalmNum = adjustPsalmForLanguage(basePsalmNum, language);
    const proverbNum = (dayOfYear % 31) + 1;
    
    return {
      date,
      oldTestament: `${bookNames[otReading.book.key]} ${otReading.chapter}`,
      newTestament: `${bookNames[ntReading.book.key]} ${ntReading.chapter}`,
      psalm: `${bookNames.psalms} ${adjustedPsalmNum}`,
      proverb: `${bookNames.proverbs} ${proverbNum}`
    };
  };

  const reading = useMemo(() => generateDailyReading(currentDate), [currentDate, language]);

  const goToPreviousDay = () => {
    setCurrentDate(subDays(currentDate, 1));
  };

  const goToNextDay = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const openInBible = (reference: string) => {
    navigate('/bible', { state: { chapterReference: reference } });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-10 h-10 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {t("dailyreading.title")}
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("dailyreading.subtitle")}
            </p>
          </div>

          {/* Date Navigation */}
          <Card className="max-w-3xl mx-auto mb-8 p-6">
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                size="icon"
                onClick={goToPreviousDay}
                className="hover:bg-primary hover:text-primary-foreground"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <h2 className="text-2xl font-semibold text-foreground">
                    {format(currentDate, 'MMMM d, yyyy')}
                  </h2>
                </div>
                {!isToday && (
                  <Button
                    variant="link"
                    onClick={goToToday}
                    className="text-sm"
                  >
                    {t("dailyreading.gototoday")}
                  </Button>
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={goToNextDay}
                className="hover:bg-primary hover:text-primary-foreground"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Reading List */}
            <div className="space-y-6">
              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t("dailyreading.oldtestament")}
                </h3>
                <button
                  onClick={() => openInBible(reading.oldTestament)}
                  className="text-xl text-muted-foreground hover:text-primary hover:underline transition-colors cursor-pointer text-left"
                >
                  {reading.oldTestament}
                </button>
              </div>

              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t("dailyreading.newtestament")}
                </h3>
                <button
                  onClick={() => openInBible(reading.newTestament)}
                  className="text-xl text-muted-foreground hover:text-primary hover:underline transition-colors cursor-pointer text-left"
                >
                  {reading.newTestament}
                </button>
              </div>

              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t("dailyreading.psalm")}
                </h3>
                <button
                  onClick={() => openInBible(reading.psalm)}
                  className="text-xl text-muted-foreground hover:text-primary hover:underline transition-colors cursor-pointer text-left"
                >
                  {reading.psalm}
                </button>
              </div>

              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t("dailyreading.proverb")}
                </h3>
                <button
                  onClick={() => openInBible(reading.proverb)}
                  className="text-xl text-muted-foreground hover:text-primary hover:underline transition-colors cursor-pointer text-left"
                >
                  {reading.proverb}
                </button>
              </div>
            </div>
          </Card>

          {/* Info Section */}
          <Card className="max-w-3xl mx-auto p-6 bg-primary/5">
            <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {t("dailyreading.about.title")}
            </h3>
            <div className="space-y-3 text-muted-foreground">
              <p>
                {t("dailyreading.about.text1")}
              </p>
              <p>
                <strong className="text-foreground">{t("dailyreading.about.benefits")}</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t("dailyreading.about.benefit1")}</li>
                <li>{t("dailyreading.about.benefit2")}</li>
                <li>{t("dailyreading.about.benefit3")}</li>
                <li>{t("dailyreading.about.benefit4")}</li>
              </ul>
              <p className="pt-4">
                <em>{t("dailyreading.about.verse")}</em>
              </p>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DailyBibleReading;
